import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import {
  GetApp as ExportIcon,
  Code as QueryIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  TableChart as TableIcon,
  FindInPage as DuplicateIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { databaseAPI } from "../services/api.js";

const QueryResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Get query data from navigation state
  const queryData = location.state?.queryData;
  const queryResults = location.state?.queryResults;
  const generatedQuery = location.state?.generatedQuery;

  // Handle data structure - queryResults might be a flat array or an object with results property
  const resultsData = Array.isArray(queryResults)
    ? queryResults
    : queryResults?.results || queryResults?.data || [];

  // Local state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showQuery, setShowQuery] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [expandedCells, setExpandedCells] = useState(new Set());

  // Duplicate detection state
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [isDuplicateScanning, setIsDuplicateScanning] = useState(false);
  const [duplicateTaskId, setDuplicateTaskId] = useState(null);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [showDuplicateControls, setShowDuplicateControls] = useState(false);
  const [duplicateProgress, setDuplicateProgress] = useState(0);
  const [deletedRecords, setDeletedRecords] = useState(new Set());

  // Inline duplicate detection state
  const [inlineDuplicates, setInlineDuplicates] = useState(new Set());
  const [selectedInlineDuplicates, setSelectedInlineDuplicates] = useState(
    new Set()
  );
  const [showInlineDuplicateMode, setShowInlineDuplicateMode] = useState(false);
  const [duplicateDetectionMode, setDuplicateDetectionMode] = useState("smart"); // exact, key-columns, smart, plant-name
  const [selectedDuplicateColumns, setSelectedDuplicateColumns] = useState([]);
  const [showDuplicateSettings, setShowDuplicateSettings] = useState(false);

  // Export mutation
  const exportDataMutation = useMutation({
    mutationFn: async (exportData) => {
      const start = await databaseAPI.exportData(exportData);
      const taskId = start.data.task_id;
      let attempts = 0;
      let statusData = null;
      while (attempts < 60) {
        const status = await databaseAPI.getExportStatus(taskId);
        if (status.status === 200 && status.data.download_url) {
          statusData = status.data;
          break;
        }
        await new Promise((r) => setTimeout(r, 5000));
        attempts += 1;
      }
      if (!statusData) throw new Error("Export timed out");
      // Fetch blob via authenticated request to avoid missing Authorization header
      const blobRes = await databaseAPI.downloadExport(taskId);
      return { taskId, meta: statusData, blob: blobRes.data };
    },
    onSuccess: ({ taskId, meta, blob }) => {
      try {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.download = meta.filename || `export.${exportFormat}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar("Export ready. Download started.", { variant: "success" });
      } catch (e) {
        enqueueSnackbar("Export ready but download failed", { variant: "warning" });
      }
      setExportDialog(false);
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || error.message || "Export failed", {
        variant: "error",
      });
    },
  });

  // Duplicate detection mutation
  const findDuplicatesMutation = useMutation({
    mutationFn: (duplicateData) => databaseAPI.findDuplicates(duplicateData),
    onSuccess: (data) => {
      setDuplicateTaskId(data.data.task_id);
      setIsDuplicateScanning(true);
      enqueueSnackbar("Duplicate detection started", { variant: "info" });
      pollDuplicateResults(data.data.task_id);
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to start duplicate detection",
        { variant: "error" }
      );
      setIsDuplicateScanning(false);
    },
  });

  // Remove duplicates mutation
  const removeDuplicatesMutation = useMutation({
    mutationFn: (removeData) => databaseAPI.removeDuplicates(removeData),
    onSuccess: (data) => {
      enqueueSnackbar("Duplicate removal started", { variant: "info" });
      // Mark records as deleted locally for immediate UI feedback
      const recordsToDelete = selectedDuplicates.map((recordKey) => {
        const parts = recordKey.split("_");
        return parts[parts.length - 1]; // Get the record ID
      });
      setDeletedRecords((prev) => new Set([...prev, ...recordsToDelete]));
      setSelectedDuplicates([]);
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to remove duplicates",
        { variant: "error" }
      );
    },
  });

  // Polling function for duplicate results
  const pollDuplicateResults = async (taskId) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await databaseAPI.getDuplicateResults(taskId);
        const data = response.data;

        if (data.status === "completed") {
          setIsDuplicateScanning(false);
          setDuplicateProgress(100);

          if (data.results && data.results.length > 0) {
            setDuplicateGroups(data.results);
            setShowDuplicateControls(true);
            enqueueSnackbar(`Found ${data.results.length} duplicate groups`, {
              variant: "success",
            });
          } else {
            enqueueSnackbar("No duplicates found", { variant: "info" });
          }
          return;
        }

        if (data.status === "failed") {
          setIsDuplicateScanning(false);
          enqueueSnackbar("Duplicate detection failed", { variant: "error" });
          return;
        }

        // Update progress
        if (data.progress !== undefined) {
          setDuplicateProgress(data.progress);
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          setIsDuplicateScanning(false);
          enqueueSnackbar("Duplicate detection timed out", {
            variant: "warning",
          });
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setIsDuplicateScanning(false);
          enqueueSnackbar("Failed to get duplicate results", {
            variant: "error",
          });
        }
      }
    };

    poll();
  };

  // Handle inline duplicate detection on current page data
  const handleFindDuplicates = () => {
    if (!resultsData?.length) {
      enqueueSnackbar("No data available for duplicate detection", {
        variant: "warning",
      });
      return;
    }

    // Get current page data
    const currentPageData = resultsData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    // Find duplicates in current page data based on detection mode
    const duplicateIndices = new Set();
    const seen = new Map();

    currentPageData.forEach((record, index) => {
      let recordKey;

      switch (duplicateDetectionMode) {
        case "key-columns":
          // Use only selected columns for comparison
          if (selectedDuplicateColumns.length === 0) {
            enqueueSnackbar("Please select columns for duplicate detection", {
              variant: "warning",
            });
            setIsDuplicateScanning(false);
            return;
          }
          recordKey = selectedDuplicateColumns
            .map((col) => `${col}:${record[col]}`)
            .sort()
            .join("|");
          break;

        case "smart":
          // Smart detection: use meaningful columns, skip IDs and calculated fields
          const meaningfulColumns = Object.entries(record).filter(
            ([key, value]) => {
              // Skip common ID fields
              if (key.toLowerCase().includes("id")) return false;

              // Skip common calculated/derived fields (scores, percentages, etc.)
              const calculatedFields = [
                "score",
                "accuracy",
                "precision",
                "recall",
                "f1",
                "percentage",
                "rate",
                "ratio",
              ];
              if (
                calculatedFields.some((field) =>
                  key.toLowerCase().includes(field)
                )
              )
                return false;

              // Skip null/undefined values
              if (value === null || value === undefined || value === "")
                return false;

              return true;
            }
          );

          recordKey = meaningfulColumns
            .map(
              ([key, value]) => `${key}:${String(value).toLowerCase().trim()}`
            )
            .sort()
            .join("|");
          break;

        case "exact":
        default:
          // Create a key from all record values (excluding id)
          recordKey = Object.entries(record)
            .filter(([key]) => key !== "id")
            .map(([key, value]) => `${key}:${value}`)
            .sort()
            .join("|");
          break;
      }

      if (seen.has(recordKey)) {
        // Mark both current and previously seen record as duplicates
        duplicateIndices.add(page * rowsPerPage + index);
        duplicateIndices.add(seen.get(recordKey));
      } else {
        seen.set(recordKey, page * rowsPerPage + index);
      }
    });

    setInlineDuplicates(duplicateIndices);
    setShowInlineDuplicateMode(true);
    setIsDuplicateScanning(false);

    if (duplicateIndices.size > 0) {
      enqueueSnackbar(
        `Found ${duplicateIndices.size} duplicate records on this page using ${duplicateDetectionMode} detection`,
        { variant: "info" }
      );
    } else {
      enqueueSnackbar(
        `No duplicates found on this page using ${duplicateDetectionMode} detection`,
        { variant: "success" }
      );
    }
  };

  // Handle inline duplicate selection
  const handleInlineDuplicateSelect = (recordIndex, checked) => {
    setSelectedInlineDuplicates((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(recordIndex);
      } else {
        newSet.delete(recordIndex);
      }
      return newSet;
    });
  };

  // Handle bulk inline duplicate selection
  const handleBulkInlineDuplicateSelect = (action) => {
    if (action === "all") {
      setSelectedInlineDuplicates(new Set(inlineDuplicates));
    } else if (action === "none") {
      setSelectedInlineDuplicates(new Set());
    }
  };

  // Handle inline duplicate removal
  const handleRemoveInlineDuplicates = () => {
    if (selectedInlineDuplicates.size === 0) {
      enqueueSnackbar("No duplicates selected for removal", {
        variant: "warning",
      });
      return;
    }

    // Remove selected duplicates from the results array
    const indicesToRemove = Array.from(selectedInlineDuplicates).sort(
      (a, b) => b - a
    );
    const newResults = [...resultsData];

    indicesToRemove.forEach((index) => {
      newResults.splice(index, 1);
    });

    // Update the query results
    const updatedQueryResults = {
      ...queryResults,
      results: newResults,
    };

    // Update the location state to persist changes
    window.history.replaceState(
      {
        ...location.state,
        queryResults: updatedQueryResults,
      },
      ""
    );

    // Reset duplicate detection state
    setInlineDuplicates(new Set());
    setSelectedInlineDuplicates(new Set());
    setShowInlineDuplicateMode(false);

    // Reset to first page if current page is now empty
    const newTotalRecords = newResults.length;
    const maxPage = Math.ceil(newTotalRecords / rowsPerPage) - 1;
    if (page > maxPage && maxPage >= 0) {
      setPage(maxPage);
    }

    enqueueSnackbar(
      `Removed ${selectedInlineDuplicates.size} duplicate records`,
      { variant: "success" }
    );
  };

  // Check if a record is an inline duplicate
  const isInlineDuplicateRecord = (recordIndex) => {
    return inlineDuplicates.has(recordIndex);
  };

  // Check if a record is deleted
  const isRecordDeleted = (record) => {
    return deletedRecords.has(String(record.id));
  };

  const handleExportData = () => {
    if (!resultsData?.length) {
      enqueueSnackbar("No data to export", { variant: "warning" });
      return;
    }

    // Filter out deleted records for export
    const cleanedData = resultsData.filter(
      (record) => !isRecordDeleted(record)
    );

    // Add suffix to filename if duplicates were removed
    const removedCount = resultsData.length - cleanedData.length;
    const filenameSuffix = removedCount > 0 ? "_deduplicated" : "";

    exportDataMutation.mutate({
      data: cleanedData,
      format: exportFormat,
      filename: `query_results${filenameSuffix}_${new Date().toISOString().split("T")[0]}`,
    });

    if (removedCount > 0) {
      enqueueSnackbar(
        `Exporting ${cleanedData.length} records (${removedCount} duplicates removed)`,
        { variant: "info" }
      );
    }
  };

  const handleBackToQueryBuilder = () => {
    navigate("/query-builder");
  };

  // If no query data, redirect back
  if (!queryData || !queryResults) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No query results to display. Please execute a query first.
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={handleBackToQueryBuilder}
        >
          Back to Query Builder
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Query Results
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Results from your database query execution
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackToQueryBuilder}
        >
          Back to Query Builder
        </Button>
      </Box>

      {/* Query Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <TableIcon /> Query Summary
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Chip
                  label={`${resultsData?.length || 0} Records`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${
                    queryData.selectedDatabases?.length || 0
                  } Database(s)`}
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  label={`${
                    Object.values(queryData.selectedTables || {}).filter(
                      Boolean
                    ).length
                  } Table(s)`}
                  color="info"
                  variant="outlined"
                />
              </Box>
              {queryData.selectedDatabases && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  <strong>Databases:</strong>{" "}
                  {queryData.selectedDatabases.join(", ")}
                </Typography>
              )}
              {queryData.selectedTables &&
                Object.keys(queryData.selectedTables).length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tables:</strong>{" "}
                    {Object.keys(queryData.selectedTables)
                      .filter((tableKey) => queryData.selectedTables[tableKey])
                      .join(", ")}
                  </Typography>
                )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Tooltip title="Find Duplicates">
                <Button
                  variant="outlined"
                  startIcon={<DuplicateIcon />}
                  onClick={handleFindDuplicates}
                  disabled={isDuplicateScanning || !resultsData?.length}
                  color="warning"
                >
                  {isDuplicateScanning ? "Scanning..." : "Find Duplicates"}
                </Button>
              </Tooltip>
              <Tooltip title="Duplicate Detection Settings">
                <IconButton
                  onClick={() => setShowDuplicateSettings(true)}
                  color="primary"
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Generated Query">
                <IconButton onClick={() => setShowQuery(true)}>
                  <QueryIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => setExportDialog(true)}
                disabled={!resultsData?.length}
              >
                Export
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Duplicate Detection Progress */}
      {isDuplicateScanning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DuplicateIcon color="warning" />
              <Typography variant="h6">Scanning for Duplicates...</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={duplicateProgress}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Progress: {duplicateProgress.toFixed(1)}%
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Inline Duplicate Controls */}
      {showInlineDuplicateMode && inlineDuplicates.size > 0 && (
        <Card
          sx={{
            mb: 3,
            bgcolor: "#fff7e6", // softer warning background
            border: "1px solid",
            borderColor: "warning.light",
            boxShadow: 0,
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6" sx={{ color: 'warning.dark', fontWeight: 'bold' }}>
                  Duplicate Detection Mode ({inlineDuplicates.size} duplicates
                  found on this page)
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  setShowInlineDuplicateMode(false);
                  setInlineDuplicates(new Set());
                  setSelectedInlineDuplicates(new Set());
                }}
              >
                Exit Duplicate Mode
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => handleBulkInlineDuplicateSelect("all")}
                disabled={inlineDuplicates.size === 0}
              >
                Select All Duplicates
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => handleBulkInlineDuplicateSelect("none")}
                disabled={selectedInlineDuplicates.size === 0}
              >
                Deselect All
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveInlineDuplicates}
                disabled={selectedInlineDuplicates.size === 0}
              >
                Remove Selected ({selectedInlineDuplicates.size})
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              Duplicate rows are highlighted in light red. Select the duplicates
              you want to remove and click "Remove Selected". This will only
              affect the current page data.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Legacy Duplicate Controls */}
      {showDuplicateControls && duplicateGroups.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6">
                  Cross-Table Duplicate Management ({duplicateGroups.length}{" "}
                  groups found)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleBulkDuplicateSelect("keep-first")}
                >
                  Keep First
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleBulkDuplicateSelect("all")}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleBulkDuplicateSelect("none")}
                >
                  Clear Selection
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveDuplicates}
                  disabled={
                    selectedDuplicates.length === 0 ||
                    removeDuplicatesMutation.isPending
                  }
                >
                  {removeDuplicatesMutation.isPending
                    ? "Removing..."
                    : `Remove Selected (${selectedDuplicates.length})`}
                </Button>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Duplicate records are highlighted in light red. Select records to
              remove and click "Remove Selected" to delete them permanently.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Data Results
          </Typography>

          {resultsData && resultsData.length > 0 ? (
            <Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 600 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {/* Inline duplicate mode columns */}
                      {showInlineDuplicateMode && (
                        <>
                          <TableCell padding="checkbox">
                            <Typography variant="body2" fontWeight="bold">
                              Select
                            </Typography>
                          </TableCell>
                          <TableCell padding="none">
                            <Typography variant="body2" fontWeight="bold">
                              Action
                            </Typography>
                          </TableCell>
                        </>
                      )}
                      {/* Legacy duplicate mode column */}
                      {showDuplicateControls && (
                        <TableCell padding="checkbox">
                          <Typography variant="body2" fontWeight="bold">
                            Select
                          </Typography>
                        </TableCell>
                      )}
                      {resultsData[0] &&
                        Object.keys(resultsData[0]).map((column, colIndex) => (
                          <TableCell key={`col-${colIndex}`}>
                            <Typography variant="body2" fontWeight="bold">
                              {column}
                            </Typography>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultsData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .filter((record) => !isRecordDeleted(record))
                      .map((row, index) => {
                        const globalIndex = page * rowsPerPage + index;
                        const isDeleted = isRecordDeleted(row);
                        const isInlineDuplicate =
                          isInlineDuplicateRecord(globalIndex);

                        if (isDeleted) return null;

                        return (
                          <TableRow
                            key={`row-${globalIndex}`}
                            sx={{
                              backgroundColor: isInlineDuplicate
                                ? "#ffebee"
                                : "inherit", // Light red for duplicates
                              border: isInlineDuplicate
                                ? "1px solid #f44336"
                                : "none",
                              "&:hover": {
                                backgroundColor: isInlineDuplicate
                                  ? "#ffcdd2"
                                  : "action.hover",
                              },
                            }}
                          >
                            {/* Inline duplicate mode columns */}
                            {showInlineDuplicateMode && (
                              <>
                                <TableCell padding="checkbox">
                                  {isInlineDuplicate ? (
                                    <Checkbox
                                      checked={selectedInlineDuplicates.has(
                                        globalIndex
                                      )}
                                      onChange={(e) =>
                                        handleInlineDuplicateSelect(
                                          globalIndex,
                                          e.target.checked
                                        )
                                      }
                                      color="error"
                                    />
                                  ) : null}
                                </TableCell>
                                <TableCell padding="none">
                                  {isInlineDuplicate ? (
                                    <Tooltip title="Remove this duplicate record">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          setSelectedInlineDuplicates(
                                            new Set([globalIndex])
                                          );
                                          handleRemoveInlineDuplicates();
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </TableCell>
                              </>
                            )}
                            {/* Legacy duplicate mode column */}
                            {showDuplicateControls && (
                              <TableCell padding="checkbox">
                                {/* Legacy duplicate logic would go here */}
                              </TableCell>
                            )}
                            {/* Data columns */}
                            {resultsData[0] &&
                              Object.keys(resultsData[0]).map(
                                (column, colIndex) => {
                                  const cellKey = `${globalIndex}-${colIndex}`;
                                  const rawValue = row[column];
                                  const stringValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : "-";
                                  const needsClamp = stringValue.length > 100;
                                  const isExpanded = expandedCells.has(cellKey);
                                  const cellTypographySx = {
                                    fontWeight: isInlineDuplicate ? "bold" : "normal",
                                    color: isInlineDuplicate ? "error.main" : "inherit",
                                    whiteSpace: isExpanded ? "normal" : (needsClamp ? "nowrap" : "normal"),
                                    overflow: isExpanded ? "visible" : (needsClamp ? "hidden" : "visible"),
                                    textOverflow: isExpanded ? "clip" : (needsClamp ? "ellipsis" : "clip"),
                                    maxWidth: isExpanded ? "none" : (needsClamp ? 360 : "none"),
                                    display: "block",
                                  };
                                  return (
                                    <TableCell key={`cell-${globalIndex}-${colIndex}`}>
                                      <Typography
                                        variant="body2"
                                        sx={cellTypographySx}
                                      >
                                        {needsClamp && !isExpanded
                                          ? `${stringValue.slice(0, 100)}...`
                                          : stringValue}
                                      </Typography>
                                      {needsClamp && (
                                        <Button
                                          size="small"
                                          color="primary"
                                          onClick={() => {
                                            setExpandedCells((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(cellKey)) next.delete(cellKey);
                                              else next.add(cellKey);
                                              return next;
                                            });
                                          }}
                                          sx={{ mt: 0.5, textTransform: "none", p: 0, minWidth: 0 }}
                                        >
                                          {isExpanded ? "Read less" : "Read more"}
                                        </Button>
                                      )}
                                    </TableCell>
                                  );
                                }
                              )}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={resultsData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <TableIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary">
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your query returned no data
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Generated Query Dialog */}
      <Dialog
        open={showQuery}
        onClose={() => setShowQuery(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generated SQL Query</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={generatedQuery || ""}
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { fontFamily: "monospace", fontSize: "14px" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuery(false)}>Close</Button>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(generatedQuery || "");
              enqueueSnackbar("Query copied to clipboard", {
                variant: "success",
              });
            }}
            variant="contained"
          >
            Copy Query
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialog}
        onClose={() => setExportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will export{" "}
            {resultsData?.filter((record) => !isRecordDeleted(record)).length ||
              0}{" "}
            records in {exportFormat.toUpperCase()} format.
            {resultsData &&
              resultsData.length !==
                resultsData.filter((record) => !isRecordDeleted(record))
                  .length && (
                <>
                  <br />
                  Note:{" "}
                  {resultsData.length -
                    resultsData.filter((record) => !isRecordDeleted(record))
                      .length}{" "}
                  duplicate records will be excluded from export.
                </>
              )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          <Button
            onClick={handleExportData}
            variant="contained"
            disabled={exportDataMutation.isLoading}
            startIcon={
              exportDataMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <ExportIcon />
              )
            }
          >
            {exportDataMutation.isLoading ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Detection Settings Dialog */}
      <Dialog
        open={showDuplicateSettings}
        onClose={() => setShowDuplicateSettings(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Duplicate Detection Settings</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Detection Mode</InputLabel>
            <Select
              value={duplicateDetectionMode}
              onChange={(e) => setDuplicateDetectionMode(e.target.value)}
              label="Detection Mode"
            >
              <MenuItem value="smart">Smart Detection (Recommended)</MenuItem>
              <MenuItem value="key-columns">Key Columns Only</MenuItem>
              <MenuItem value="exact">Exact Match (All Fields)</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Smart Detection:</strong> Compares meaningful data
              columns, ignores IDs and calculated fields (scores, percentages,
              etc.)
              <br />
              <strong>Key Columns:</strong> Compare only selected columns for
              duplicates
              <br />
              <strong>Exact Match:</strong> All fields must match exactly (very
              strict)
            </Typography>
          </Box>

          {duplicateDetectionMode === "key-columns" && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Columns for Comparison</InputLabel>
              <Select
                multiple
                value={selectedDuplicateColumns}
                onChange={(e) => setSelectedDuplicateColumns(e.target.value)}
                input={<OutlinedInput label="Select Columns for Comparison" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {resultsData?.[0] &&
                  Object.keys(resultsData[0])
                    .filter((key) => key !== "id")
                    .map((column) => (
                      <MenuItem key={column} value={column}>
                        <Checkbox
                          checked={
                            selectedDuplicateColumns.indexOf(column) > -1
                          }
                        />
                        <ListItemText primary={column} />
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          )}

          {duplicateDetectionMode === "smart" && resultsData?.[0] && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Smart Detection will compare these columns:</strong>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {Object.entries(resultsData[0])
                  .filter(([key, value]) => {
                    // Skip common ID fields
                    if (key.toLowerCase().includes("id")) return false;

                    // Skip common calculated/derived fields
                    const calculatedFields = [
                      "score",
                      "accuracy",
                      "precision",
                      "recall",
                      "f1",
                      "percentage",
                      "rate",
                      "ratio",
                    ];
                    if (
                      calculatedFields.some((field) =>
                        key.toLowerCase().includes(field)
                      )
                    )
                      return false;

                    // Skip null/undefined values
                    if (value === null || value === undefined || value === "")
                      return false;

                    return true;
                  })
                  .map(([key]) => (
                    <Chip
                      key={key}
                      label={key}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
              </Box>
              <Alert severity="info" sx={{ mt: 1 }}>
                Automatically excludes ID fields and calculated values (scores,
                percentages, etc.) for better duplicate detection
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDuplicateSettings(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowDuplicateSettings(false);
              enqueueSnackbar(
                `Duplicate detection mode set to: ${duplicateDetectionMode}`,
                { variant: "success" }
              );
            }}
            variant="contained"
          >
            Apply Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueryResults;
